import { useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, MapPin, BedDouble, Bath } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { Eyebrow } from "@/components/common/Section";
import {
  getCity,
  getType,
  listingsBy,
  STATUS_LABEL,
} from "@/data/rentals";

const money = (n) => `$${n.toLocaleString("en-CA")}`;

/**
 * /rentals/:city/:type — filterable listings grid.
 */
export default function RentalType() {
  const { city: citySlug, type: typeSlug } = useParams();
  const city = getCity(citySlug);
  const type = getType(typeSlug);

  const [statusFilter, setStatusFilter] = useState("all");
  const [neighbourhood, setNeighbourhood] = useState("all");
  const [sort, setSort] = useState("price-asc");

  const all = useMemo(
    () => (city && type ? listingsBy({ city: city.slug, type: type.slug }) : []),
    [city, type],
  );
  const neighbourhoods = useMemo(
    () => Array.from(new Set(all.map((l) => l.neighbourhood))).sort(),
    [all],
  );

  const filtered = useMemo(() => {
    let list = [...all];
    if (statusFilter !== "all") list = list.filter((l) => l.status === statusFilter);
    if (neighbourhood !== "all")
      list = list.filter((l) => l.neighbourhood === neighbourhood);
    if (sort === "price-asc") list.sort((a, b) => a.monthlyRent - b.monthlyRent);
    if (sort === "price-desc") list.sort((a, b) => b.monthlyRent - a.monthlyRent);
    if (sort === "size-desc") list.sort((a, b) => b.specs.sqft - a.specs.sqft);
    return list;
  }, [all, statusFilter, neighbourhood, sort]);

  if (!city || !type) return <Navigate to="/rentals" replace />;

  return (
    <div data-testid={`page-rental-type-${type.slug}`}>
      {/* ============ HEADER ============ */}
      <header
        className="relative"
        style={{ backgroundColor: "#EDE9E0" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 pb-14 pt-[128px] md:px-10 md:pb-16 md:pt-[168px]">
          <Reveal>
            <div className="flex items-center gap-2">
              <Link
                to={`/rentals/${city.slug}`}
                className="group inline-flex items-center gap-2"
                style={{ color: "rgba(32,30,26,0.7)" }}
                data-testid="back-to-city"
              >
                <ArrowLeft size={16} />
                <span className="label-caps text-[11px]">{city.name}</span>
              </Link>
            </div>
          </Reveal>

          <div className="mt-6 grid grid-cols-1 items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <Eyebrow>
                  {city.name} · {type.name}
                </Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1
                  className="font-display mt-6"
                  style={{
                    fontSize: "clamp(44px, 6.5vw, 96px)",
                    color: "#201E1A",
                    lineHeight: 0.95,
                    letterSpacing: "-0.015em",
                  }}
                  data-testid="rental-type-title"
                >
                  {type.name} in{" "}
                  <span className="italic" style={{ color: "#C89A4E" }}>
                    {city.name}
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.12}>
                <p
                  className="body-measure mt-5 text-[17px]"
                  style={{ color: "rgba(32,30,26,0.72)" }}
                >
                  {type.tagline}
                </p>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.15}>
                <p
                  className="font-display"
                  style={{
                    fontSize: 48,
                    color: "#24382E",
                    lineHeight: 0.9,
                  }}
                  data-testid="rental-type-count"
                >
                  {filtered.length}
                </p>
                <p
                  className="label-caps mt-2 text-[11px]"
                  style={{ color: "rgba(32,30,26,0.6)" }}
                >
                  Showing of {all.length}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </header>

      {/* ============ FILTER BAR ============ */}
      <div
        className="sticky top-[72px] z-40 border-y"
        style={{
          backgroundColor: "rgba(248,245,239,0.92)",
          backdropFilter: "blur(14px)",
          borderColor: "rgba(32,30,26,0.08)",
        }}
        data-testid="rental-filter-bar"
      >
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-4 px-6 py-4 md:px-10">
          <FilterGroup label="Status">
            <FilterChip
              active={statusFilter === "all"}
              onClick={() => setStatusFilter("all")}
              testId="filter-status-all"
            >
              All
            </FilterChip>
            <FilterChip
              active={statusFilter === "available"}
              onClick={() => setStatusFilter("available")}
              testId="filter-status-available"
            >
              Available
            </FilterChip>
            <FilterChip
              active={statusFilter === "coming-soon"}
              onClick={() => setStatusFilter("coming-soon")}
              testId="filter-status-coming"
            >
              Coming soon
            </FilterChip>
          </FilterGroup>

          {neighbourhoods.length > 1 && (
            <FilterGroup label="Neighbourhood">
              <FilterChip
                active={neighbourhood === "all"}
                onClick={() => setNeighbourhood("all")}
                testId="filter-nb-all"
              >
                All
              </FilterChip>
              {neighbourhoods.map((nb) => (
                <FilterChip
                  key={nb}
                  active={neighbourhood === nb}
                  onClick={() => setNeighbourhood(nb)}
                  testId={`filter-nb-${nb.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {nb}
                </FilterChip>
              ))}
            </FilterGroup>
          )}

          <div className="ml-auto flex items-center gap-3">
            <span
              className="label-caps text-[10px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Sort
            </span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-full border bg-transparent px-4 py-1.5 text-[13px] outline-none"
              style={{
                borderColor: "rgba(32,30,26,0.2)",
                color: "#24382E",
              }}
              data-testid="filter-sort"
            >
              <option value="price-asc">Price · low to high</option>
              <option value="price-desc">Price · high to low</option>
              <option value="size-desc">Size · largest first</option>
            </select>
          </div>
        </div>
      </div>

      {/* ============ LISTINGS GRID ============ */}
      <section
        className="relative"
        style={{ backgroundColor: "#F8F5EF" }}
        data-nav-theme="light"
      >
        <div className="mx-auto max-w-[1440px] px-6 py-20 md:px-10 md:py-28">
          {filtered.length === 0 ? (
            <div className="py-16 text-center">
              <p
                className="font-display"
                style={{ fontSize: 32, color: "#24382E" }}
              >
                Nothing quite matches that.
              </p>
              <p
                className="mt-3 text-[15px]"
                style={{ color: "rgba(32,30,26,0.7)" }}
              >
                Try widening the filters, or{" "}
                <Link to="/contact" className="underline">
                  tell us what you&rsquo;re after
                </Link>{" "}
                and we&rsquo;ll keep an eye out.
              </p>
            </div>
          ) : (
            <ul
              className="grid grid-cols-1 gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3"
              data-testid="rental-listings-grid"
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((l, i) => (
                  <motion.li
                    key={l.slug}
                    layout
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{
                      duration: 0.5,
                      delay: (i % 3) * 0.05,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  >
                    <ListingCard listing={l} city={city} type={type} />
                  </motion.li>
                ))}
              </AnimatePresence>
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

/* ---------------- helpers ---------------- */

const FilterGroup = ({ label, children }) => (
  <div className="flex items-center gap-2">
    <span
      className="label-caps text-[10px]"
      style={{ color: "rgba(32,30,26,0.55)" }}
    >
      {label}
    </span>
    <div className="flex flex-wrap gap-1.5">{children}</div>
  </div>
);

const FilterChip = ({ active, onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="rounded-full border px-3 py-1 text-[12px] transition-colors"
    style={{
      borderColor: active ? "#24382E" : "rgba(32,30,26,0.16)",
      backgroundColor: active ? "#24382E" : "transparent",
      color: active ? "#F8F5EF" : "#24382E",
    }}
  >
    {children}
  </button>
);

const ListingCard = ({ listing, city, type }) => {
  const status = STATUS_LABEL[listing.status] || STATUS_LABEL.available;
  const to = `/rentals/${city.slug}/${type.slug}/${listing.slug}`;
  return (
    <Link
      to={to}
      className="group block"
      data-testid={`listing-card-${listing.slug}`}
    >
      <div
        className="relative overflow-hidden"
        style={{
          borderRadius: 16,
          aspectRatio: "4 / 5",
          backgroundColor: "#E4DFD3",
        }}
      >
        <img
          src={listing.hero}
          alt={`${listing.name} ${listing.unit}`}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(20,35,27,0) 55%, rgba(20,35,27,0.42) 100%)",
          }}
        />
        <span
          className="absolute left-4 top-4 label-caps rounded-full px-3 py-1 text-[10px]"
          style={{
            backgroundColor: status.bg,
            color: status.color,
          }}
        >
          {status.label}
        </span>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
          <p
            className="font-display"
            style={{
              color: "#F8F5EF",
              fontSize: 22,
              lineHeight: 1,
              textShadow: "0 2px 12px rgba(20,35,27,0.6)",
            }}
          >
            {money(listing.monthlyRent)}
            <span
              className="ml-1 text-[12px]"
              style={{ color: "rgba(248,245,239,0.7)" }}
            >
              /mo
            </span>
          </p>
          <ArrowUpRight
            size={18}
            color="#F8F5EF"
            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </div>
      </div>

      <div className="mt-5">
        <h3
          className="font-display"
          style={{
            fontSize: 22,
            color: "#201E1A",
            lineHeight: 1.1,
            letterSpacing: "-0.005em",
          }}
        >
          {listing.name}{" "}
          <span style={{ color: "rgba(32,30,26,0.45)" }}>{listing.unit}</span>
        </h3>
        <p
          className="mt-1 inline-flex items-center gap-1.5 text-[13px]"
          style={{ color: "rgba(32,30,26,0.6)" }}
        >
          <MapPin size={13} />
          {listing.neighbourhood}
        </p>

        <div
          className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[12px]"
          style={{ color: "rgba(32,30,26,0.7)" }}
        >
          <span className="inline-flex items-center gap-1">
            <BedDouble size={13} /> {listing.specs.beds} bed
            {listing.specs.beds === 1 ? "" : "s"}
          </span>
          <span className="inline-flex items-center gap-1">
            <Bath size={13} /> {listing.specs.baths} bath
            {listing.specs.baths === 1 ? "" : "s"}
          </span>
          <span>{listing.specs.sqft} sqft</span>
        </div>
      </div>
    </Link>
  );
};
